import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const ChipChipAcceptedDaily: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [acceptedStartDate, setAcceptedStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [acceptedEndDate, setAcceptedEndDate] = useState<Date>(new Date());
  const [isAcceptedSyncing, setIsAcceptedSyncing] = useState(false);

  const { data: acceptedRows } = useQuery({
    queryKey: ['chipchip-daily-accepted-totals', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_accepted_totals')
        .select('date, total_orders, total_quantity, total_gmv')
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .lte('date', format(endDate, 'yyyy-MM-dd'))
        .order('date', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return data as Array<{ date: string; total_orders: number; total_quantity: number; total_gmv: number }>;
    },
  });

  const acceptedSummary = acceptedRows ? {
    orders: acceptedRows.reduce((s, r) => s + Number(r.total_orders || 0), 0),
    quantity: acceptedRows.reduce((s, r) => s + Number(r.total_quantity || 0), 0),
    gmv: acceptedRows.reduce((s, r) => s + Number(r.total_gmv || 0), 0),
    days: acceptedRows.length,
  } : null;

  const { data: acceptedCoverage } = useQuery({
    queryKey: ['chipchip-accepted-coverage'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_accepted_totals')
        .select('date, synced_at')
        .order('date', { ascending: true })
        .limit(10000);
      if (error) throw error;
      const rows = (data || []) as Array<{ date: string; synced_at: string }>;
      if (!rows.length) return null;
      const sortedSync = [...rows].sort((a, b) => (a.synced_at < b.synced_at ? 1 : -1));
      return {
        minDate: rows[0].date,
        maxDate: rows[rows.length - 1].date,
        totalDays: rows.length,
        lastSyncedAt: sortedSync[0]?.synced_at,
      };
    },
  });

  const handleSyncAccepted = async () => {
    if (acceptedStartDate > acceptedEndDate) {
      toast({ title: 'Invalid Range', description: 'Start date must be before end date', variant: 'destructive' });
      return;
    }
    setIsAcceptedSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('chipchip-accepted-daily-sync', {
        body: {
          startDate: format(acceptedStartDate, 'yyyy-MM-dd'),
          endDate: format(acceptedEndDate, 'yyyy-MM-dd'),
        },
      });
      if (error) throw error;
      toast({
        title: 'Accepted Totals Synced',
        description: `Synced ${data?.days ?? 0} day(s): ${(data?.totals?.orders ?? 0).toLocaleString()} orders, GMV ${(data?.totals?.gmv ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      });
      queryClient.invalidateQueries({ queryKey: ['chipchip-daily-accepted-totals'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-accepted-coverage'] });
    } catch (err: any) {
      toast({ title: 'Accepted Sync Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsAcceptedSyncing(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold">Orders / Quantity / GMV</h1>
          <p className="text-sm text-muted-foreground">
            Daily accepted-order totals from ChipChip (excludes REJECTED_BY_VENDOR), bucketed by order day (Africa/Nairobi).
          </p>
        </div>

        {/* Filter range */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">View From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">View To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(endDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Badge variant="outline">
                {acceptedSummary?.days ?? 0} day{(acceptedSummary?.days ?? 0) === 1 ? '' : 's'} in current filter
              </Badge>
              {acceptedCoverage && (
                <Badge variant="secondary" title={`Last synced: ${format(new Date(acceptedCoverage.lastSyncedAt), 'PPpp')}`}>
                  DB coverage: {format(new Date(acceptedCoverage.minDate), 'MMM dd, yyyy')} – {format(new Date(acceptedCoverage.maxDate), 'MMM dd, yyyy')} ({acceptedCoverage.totalDays} days)
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sync range */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Sync From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(acceptedStartDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={acceptedStartDate} onSelect={(d) => d && setAcceptedStartDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Sync To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(acceptedEndDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={acceptedEndDate} onSelect={(d) => d && setAcceptedEndDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Button onClick={handleSyncAccepted} size="sm" disabled={isAcceptedSyncing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isAcceptedSyncing && 'animate-spin')} />
                {isAcceptedSyncing ? 'Syncing...' : 'Sync Accepted Totals for Range'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {acceptedSummary && acceptedSummary.days > 0 ? (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.orders.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">In current page filter</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  <p className="text-xs text-muted-foreground">In current page filter</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total GMV</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.gmv.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  <p className="text-xs text-muted-foreground">Price × Quantity</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acceptedRows!.map((r) => (
                      <TableRow key={r.date}>
                        <TableCell>{format(new Date(r.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell className="text-right">{Number(r.total_orders).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{Number(r.total_quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">{Number(r.total_gmv).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No accepted-daily data for this range yet. Click "Sync Accepted Totals for Range" to fetch.</p>
        )}
      </div>
    </Layout>
  );
};

export default ChipChipAcceptedDaily;
